'use client';
import type { Session } from 'next-auth';
import { useRouter, useSearchParams } from 'next/navigation';
import { type FC, type FormEvent, useRef, useState } from 'react';
import { getSession, signIn, signOut, useSession } from 'next-auth/react';
import {
  ChevronRight,
  PasswordOutline,
  TwoUsers,
  View,
  ViewOff,
} from '@eqi-investimentos/icons/web';
import {
  Flex,
  Link,
  Text,
  Button,
  Input,
  FormControl,
  IconButton,
  Divider,
} from '@eqi-investimentos/react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { captureException } from '@sentry/nextjs';
import { object, string } from 'yup';
import { getFeatureFlag } from '@/libs/firebase/firebaseConfig';
import { useT } from '@/libs/i18n/utils/useT';
import { appConfig } from '@/config/app';
import { envConfig } from '@/config/env';
import { PATHS } from '@/config/paths';
import { MAINTENANCE_CHECK_STATE_EVENT } from '@/hooks/useMaintenance';
import { Thumb } from '@/components/elements/Thumb';
import { getLogoByTheme } from '@/utils/assets';
import { sanitizeNumber } from '@/utils/format';
import { formatCPF } from '@/utils/masks';
import { validateCPF } from '@/utils/validate';
import { InfoDownloadApp } from './InfoDownloadApp';

export const Login: FC<LoginProps> = ({ initialSession }) => {
  const recaptchaRef = useRef<
    (ReCAPTCHA & { captcha: HTMLDivElement | null }) | null
  >(null);
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const { data: session = initialSession } = useSession();
  const t = useT();

  const hasUserSaved =
    !!session &&
    !!session.user &&
    (!!session.user.document || !!session.user.email);

  const validationSchema = object().shape({
    email: hasUserSaved
      ? string().nullable()
      : string()
          .required(t('formValidation.fieldRequired'))
          .test('email-or-cpf', function (value) {
            if (hasUserSaved) {
              return true;
            }

            const isEmail = value.includes('@');

            if (isEmail && !string().email().isValidSync(value)) {
              return this.createError({
                message: t('formValidation.invalidEmail'),
              });
            }

            if (!isEmail && !validateCPF(value)) {
              return this.createError({
                message: t('formValidation.invalidCpf'),
              });
            }

            return true;
          }),
    password: hasUserSaved
      ? string()
          .min(8, t('formValidation.passMinLength'))
          .required(t('formValidation.fieldRequired'))
      : string().nullable(),
    captcha_value: string().nullable(),
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    setError,
  } = useForm<LoginForm>({
    resolver: yupResolver(validationSchema),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [hasToUpdate, setHasToUpdate] = useState(false);

  const redirect_url = searchParams.get('redirect_url');

  const showPasswordStep =
    !!session && !!session.user && session.user.masked_name;
  const maskedName = showPasswordStep || '';

  const onChangeEmailInput = (text: string) => {
    const numericRegex = /^\d+$/;
    const isJustNumbers = numericRegex.test(
      text.replace(/\./g, '').replace(/-/g, '')
    );

    if (isJustNumbers && text.length > 4 && !text.includes('@')) {
      const value = formatCPF(text);
      setValue('email', value);
      return;
    }

    const value = text.replace(/-/g, '');
    setValue('email', value);
  };

  const handleEmailOrCPFForm = async (
    emailOrCPF?: string | null,
    captcha_value?: string | null
  ) => {
    const isInputTypeEmail = watch('email')!.includes('@');

    const identifier =
      (isInputTypeEmail ? emailOrCPF?.trim() : sanitizeNumber(emailOrCPF!)) ??
      '';

    const response = await signIn('identify-customer', {
      identifier,
      captcha: captcha_value ?? '',
      redirect: false,
    });

    const newSession = await getSession();

    if (!newSession || !response || response.error) {
      return;
    }

    const { masked_name, isFirstAccess, hasToUpdate } = newSession.user;

    setHasToUpdate(!!hasToUpdate);

    if (!masked_name) return push(PATHS.profile.register);
    if (isFirstAccess) return push(PATHS.auth.otp);
  };

  const onSubmit = handleSubmit(async ({ email, password, captcha_value }) => {
    try {
      if (!hasUserSaved) {
        return handleEmailOrCPFForm(email, captcha_value);
      }

      const isOutOfService = await getFeatureFlag('isOutOfService');
      if (isOutOfService) {
        window.dispatchEvent(
          new CustomEvent(MAINTENANCE_CHECK_STATE_EVENT, {
            detail: { flag: 'isOutOfService' },
          })
        );
        return;
      }

      const response = await signIn('simple-login', {
        identifier: session.user.document || session.user.email,
        password,
        callbackUrl: redirect_url ?? undefined,
        redirect: Boolean(redirect_url),
      });

      const newSession = await getSession();

      if (!newSession || !response || response.error) {
        setError('password', { type: 'manual', message: 'Senha inválida' });
        return;
      }

      push(
        hasToUpdate || !newSession.user.birth_date
          ? PATHS.profile.updateData
          : PATHS.welcome
      );
    } catch (error) {
      captureException({
        tag: 'useLoginViewModel.onSubmit',
        error,
        message: 'Erro validar e-mail/CPF na tela de login',
      });
    }
  });

  const handleSubmitForm = async (e: FormEvent) => {
    e.preventDefault();

    if (recaptchaRef.current?.captcha) {
      const recaptchaValue = await recaptchaRef.current.executeAsync();
      setValue('captcha_value', recaptchaValue);

      await onSubmit();
      return recaptchaRef.current.reset();
    }

    await onSubmit();
  };

  return (
    <>
      <Flex
        flexDir={showPasswordStep ? 'row' : 'column'}
        alignItems="center"
        p="2xl"
        gap="3xl"
        borderRadius="xs"
        w="fit-content"
        h="fit-content"
        bg="background.primary"
        alignSelf="center"
      >
        <Flex
          as="form"
          onSubmit={handleSubmitForm}
          flexDir="column"
          gap="2xl"
          w="21rem"
          h="full"
        >
          <Thumb
            src={getLogoByTheme()}
            width={92}
            height={32}
            full
            fit="contain"
            alt="Logo EQI+"
          />
          <Flex flexDir="column" w="full" gap="m">
            <Text size="h5">
              {maskedName
                ? `${t('attributes.hello')}, ${maskedName}`
                : t('screens.login.title')}
            </Text>
            <Text size="xl">
              {showPasswordStep
                ? t('screens.login.subTitleLoggedBefore')
                : t('screens.login.subTitle')}
            </Text>
          </Flex>
          {!showPasswordStep && (
            <FormControl
              id="email"
              label="E-mail ou CPF"
              errorMessage={errors.email?.message}
            >
              <Input
                type="text"
                name="email"
                w="21rem"
                size="md"
                value={watch('email') ?? ''}
                onChange={({ target: { value } }) => onChangeEmailInput(value)}
              />
            </FormControl>
          )}
          {showPasswordStep && (
            <FormControl
              id="password"
              label="Senha"
              errorMessage={errors.password?.message}
            >
              <Input
                type={!showPassword ? 'password' : 'text'}
                name="password"
                value={watch('password') ?? ''}
                w="21rem"
                size="md"
                onChange={({ target: { value } }) =>
                  setValue('password', value)
                }
                rightIcon={
                  <IconButton
                    aria-label="toggle show password"
                    variant="ghost"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <ViewOff boxSize="1.25rem" color="content.brand" />
                    ) : (
                      <View boxSize="1.25rem" color="content.brand" />
                    )}
                  </IconButton>
                }
              />
            </FormControl>
          )}
          <Button
            type="submit"
            isDisabled={!watch('email') && !showPasswordStep}
            w="full"
            size="lg"
            isLoading={isSubmitting}
          >
            <Text size="label.l">
              {t(`attributes.${showPassword ? 'enter' : 'toStart'}`)}
            </Text>
          </Button>
          {showPasswordStep && (
            <>
              <Flex
                as={Link}
                href={PATHS.auth.resetPassword}
                fontSize="label.l"
                color="content.brand"
                fontWeight="semibold"
                alignSelf="center"
              >
                <Text size="xl" color="content.brand" fontWeight="semibold">
                  {t('screens.login.forgotPassword')}
                </Text>
              </Flex>
              <Flex
                alignItems="center"
                justifyContent="center"
                gap="m"
                w="full"
              >
                <Divider borderColor="border.secondary" />
                <Text size="m" color="border.secondary">
                  {t('attributes.or')}
                </Text>
                <Divider borderColor="border.secondary" />
              </Flex>
              <Flex
                as={Link}
                href={PATHS.auth.otp}
                justifyContent="space-between"
                alignItems="center"
                w="full"
                h="2.5rem"
                pb="2xl"
                borderBottom="1px solid"
                borderColor="border.secondary"
                _hover={{
                  textDecor: 'none',
                }}
              >
                <Flex gap="s" alignItems="center">
                  <PasswordOutline boxSize="1.5rem" color="content.brand" />
                  <Text size="l">{t('screens.login.enterOtp')}</Text>
                </Flex>
                <ChevronRight boxSize="1.5rem" color="content.brand" />
              </Flex>

              <Flex
                onClick={() => signOut()}
                cursor="pointer"
                justifyContent="space-between"
                alignItems="center"
                w="full"
                h="2.5rem"
                pb="2xl"
                borderBottom="1px solid"
                borderColor="border.secondary"
              >
                <Flex gap="s" alignItems="center">
                  <TwoUsers boxSize="1.5rem" color="content.brand" />
                  <Text size="l">
                    {t('screens.login.loginWithAnotherUser')}
                  </Text>
                </Flex>
                <ChevronRight boxSize="1.5rem" color="content.brand" />
              </Flex>
            </>
          )}

          {!showPasswordStep && (
            <>
              <Text
                size="m"
                fontWeight="light"
                color="content.tertiary"
                textAlign="center"
                sx={{
                  a: {
                    textDecor: 'underline',
                    cursor: 'pointer',
                    color: 'content.brand',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: t('policyPrivacy.content'),
                }}
              />
            </>
          )}

          <Text size="m">
            {`${t('drawers.profile.appVersion')} ${envConfig?.appVersion}`}
          </Text>
        </Flex>
        {showPasswordStep && <InfoDownloadApp />}
      </Flex>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={appConfig.recaptchaKey}
        size="invisible"
      />
    </>
  );
};

type LoginProps = {
  initialSession: Session | null;
};

type LoginForm = {
  email?: string | null;
  password?: string | null;
  captcha_value?: string | null;
};
